import type { ConsentScope, JourneyTierBadge } from '@tonnie/foundation';
import type { LifecycleStep, PipelineCardTag } from '@tonnie/types';
/**
 * Talent Invitations Service
 *
 * Companion to the platform-api invitation-first flow:
 *   - Citizen-facing: preview (public) + accept (auth)
 *   - Business-facing: list / create / bulk / send / resend / revoke / delete
 */

import type { InvitationKind } from '@tonnie/types';
import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';

// ── Types ────────────────────────────────────────────────────────────

export type InvitationStatus =
  'draft' | 'pending' | 'accepted' | 'expired' | 'revoked' | 'declined';

/**
 * What the caller may do with this invitation — decided SERVER-SIDE.
 *
 * The page switches on `decision`; it must never compare email addresses
 * itself. That would put an authorization rule in a component (root CLAUDE.md
 * rule 9) and create a second implementation to drift from the server's.
 *
 * `wrong_portal` is absent deliberately: journey-web runs on the citizen Clerk
 * instance, so a business JWT cannot reach this endpoint. It stays an
 * accept-time error rather than an unreachable UI branch.
 */
/**
 * What accepting a CLAIMED invitation (S5) does to the other account that holds
 * the invited address: closed if it holds no journey data, kept otherwise (D-4).
 */
export type ClaimOtherAccountOutlook = 'will_retire' | 'kept';

export type InvitationEligibility =
  | {
      decision: 'can_accept';
      invitedEmail: string;
      /** Present when the right to accept comes from a claim (Citizen first run · S5). */
      claim?: { otherAccount: ClaimOtherAccountOutlook };
    }
  | { decision: 'email_not_on_account'; invitedEmail: string; signedInEmail: string }
  | { decision: 'email_unverified'; invitedEmail: string; signedInEmail: string }
  | { decision: 'already_accepted_by_you'; talentId: string | null; journeyId: string | null }
  | { decision: 'already_accepted_by_other' }
  | { decision: 'terminal'; reason: 'expired' | 'revoked' | 'declined' };

/**
 * A pending invitation addressed to a VERIFIED address on the signed-in
 * citizen — found by query, not by token, because the premise of reconciliation
 * is that they never had the token.
 */
export interface MyPendingInvitation {
  id: string;
  /** Safe to return: the query only matches addresses they have proven. */
  token: string;
  email: string;
  destinationCountry: string | null;
  branchName: string | null;
  workLocation: string | null;
  scenario: string | null;
  invitationKind: InvitationKind;
  testitTests: string[];
  expiresAt: string | null;
  /**
   * When its "Not now" lapses — null when it was not put off. Only ever set on
   * the full list (`mine({ includeDeferred: true })`); the default list omits
   * deferred invitations altogether.
   */
  deferredUntil: string | null;
  companyName: string;
  companyLogo: string | null;
  /**
   * Set when this is not an invitation that opens a relationship but a
   * company's REQUEST for more on one that exists (D2) — answered with Share
   * (accept), Not now (defer) or Decline; null for an invitation.
   */
  request: MyShareRequest | null;
}

/**
 * What a company's request would let it see — decided by the server, rendered
 * as it says. (D2)
 */
export interface MyShareRequest {
  /** The journey of the relationship it widens. */
  journeyId: string;
  /** Their CheckUp, or specific TestIt tests. */
  asks: 'checkup' | 'tests';
  /** The tests the relationship would ask for after Share (`tests` only). */
  tests: string[];
  /** Everything the company would see after Share. */
  willSee: ConsentScope[];
  /** Of `willSee`, what Share adds — the company cannot see these now. */
  adds: ConsentScope[];
  /** What the company would still not see. */
  wontSee: ConsentScope[];
  /** The journey's CheckUp is scored: Share shows the score at once (else once it is). */
  checkupScored: boolean;
}

/** Sanitized preview returned by the public /preview endpoint. */
export interface InvitationPreview {
  id: string;
  status: InvitationStatus;
  /**
   * MASKED (`n•••••c@hotmail.com`). `/preview` is public and unauthenticated,
   * so anyone holding the link receives this payload — a forwarded link, or one
   * fetched by a mail-scanning proxy, must not disclose the recipient.
   *
   * The full address comes from `/eligibility`, which requires authentication.
   */
  email: string;
  firstName: string | null;
  lastName: string | null;
  destinationCountry: string | null;
  branchName: string | null;
  workLocation: string | null;
  scenario: string | null;
  customMessage: string | null;
  /** BL-214 — full CheckUp vs a TestIt "specific tests" invite. */
  invitationKind: InvitationKind;
  /** TestIt: the reusable-test catalog ids the citizen is asked to complete. */
  testitTests: string[];
  expiresAt: string | null;
  companyName: string;
  companyLogo: string | null;
  /**
   * D2 — a company's request for more on a relationship that exists: what it
   * asks for. Its answer (and what the company would see) is the signed-in
   * citizen's, from `mine`. Null for an invitation.
   */
  request: { asks: 'checkup' | 'tests'; tests: string[] } | null;
}

/** Pipeline-card shape for the Acquisition column. */
export interface InvitationListItem {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
  /**
   * What the company imported about the candidate — role, location, skills
   * (its own spreadsheet, or the Pool profile). Null/empty when not supplied,
   * and withheld with the name for a citizen who deleted their account.
   */
  jobTitle: string | null;
  location: string | null;
  skills: string[];
  status: InvitationStatus;
  destinationCountry: string | null;
  branchName: string | null;
  workLocation: string | null;
  cohortId: string | null;
  programId: string | null;
  sentAt: string | null;
  expiresAt: string | null;
  acceptedAt: string | null;
  /** When it closed — declined, expired or revoked; null while live. */
  closedAt: string | null;
  createdAt: string | null;

  // ── The funnel, on the card ───────────────────────────────────────────
  //
  // "Pending" used to be the whole story, and it covered four situations the
  // admin acts on differently: never delivered, delivered and unopened, opened
  // and not clicked, clicked and dead-ended. Each has its own timestamp, so the
  // card can say which.
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  landedAt: string | null;

  /**
   * "Signed up, never accepted."
   *
   * A BOOLEAN and nothing more — never the user id, never a name. The company
   * learns the person is on Tonnie, which is what makes a Resend worth sending;
   * it learns nothing about their journey, CheckUp or activity, all of which
   * still require `status='accepted'`.
   */
  inviteeHasAccount: boolean;
  /** When reconciliation tied a signup to this invitation. */
  matchedAt: string | null;
  /** Which provisioning hook noticed: 'webhook' | 'jit' | 'onboarding'. */
  matchSource: string | null;

  /**
   * The ONE axis — "what is this person waiting on" — for a row that has no
   * journey yet. (W7.1)
   *
   * The cohort roster's invitation feeder had derived this from these exact
   * inputs since W3, but the LIST did not carry it, so the Acquisition column
   * of the board had no step to read and the tag module kept a legacy branch
   * that re-derived a coarser answer from `status`. Two vocabularies, one
   * funnel.
   */
  lifecycleStep: LifecycleStep;
  /**
   * The chips this card wears, decided server-side from that step.
   * (W7.1 · DW7-2)
   */
  cardTags: PipelineCardTag[];
  /** The card's status on the board's status axis, decided by the server. */
  stageStatus: 'on-track' | 'at-risk' | 'blocked' | 'completed';
}

/**
 * Single-row detail shape powering the InvitationDetailDrawer in
 * business-web. Extends the list item with the lifecycle timeline,
 * the full opportunity payload, and a token preview (`tokenPreview`
 * is `${token.slice(0, 8)}…` — full token is never returned to the
 * admin surface).
 */
export interface InvitationDetail extends InvitationListItem {
  scenario: 'pre_immigration' | 'already_abroad' | 'relocation_only' | 'integration_only' | null;
  scope: string[] | null;
  /** BL-214 — full CheckUp vs a TestIt "specific tests" invite. */
  invitationKind: InvitationKind;
  /** TestIt: the reusable-test catalog ids chosen for this invite. */
  testitTests: string[];
  pricingPlan: string | null;
  tokenPreview: string | null;
  hasToken: boolean;
  statusHistory: Array<{
    status: string | null;
    at: string | null;
    /** Acting user id. Resolved to `byName` server-side; not rendered raw. */
    by: string | null;
    /** Display name for `by`, when the actor is a member of this tenant. */
    byName: string | null;
    reason: string | null;
    /** Step payload — e.g. the provider message id on a delivery step. */
    meta: Record<string, unknown> | null;
  }>;
  invitedBy: string | null;
  updatedAt: string | null;
  /**
   * WHICH of the invitee's addresses they proved control of when accepting.
   * Differs from `email` in exactly the case H17 exists for: invited at
   * work@corp, signed in as personal@gmail, verified work@corp on the same
   * identity.
   */
  acceptedViaEmail: string | null;
}

export interface CreateInvitationInput {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  cohortId?: string | null;
  programId?: string | null;
  destinationCountry?: string | null;
  branchName?: string | null;
  workLocation?: string | null;
  scope?: string[] | null;
  scenario?: 'pre_immigration' | 'already_abroad' | 'relocation_only' | 'integration_only' | null;
  customMessage?: string | null;
  customSubject?: string | null;
  customBody?: string | null;
  /** The template the subject / body were written from — see {@link InviteTemplateCode}. */
  customBodySource?: InviteTemplateCode | null;
  metadata?: Record<string, unknown> | null;
  /** BL-214 — full CheckUp (default) vs a TestIt "specific tests" invite. */
  invitationKind?: InvitationKind | null;
  /** TestIt: chosen reusable-test catalog ids (required when kind='testit'). */
  testitTests?: string[] | null;
  /** TestIt: optional tighter reuse-freshness window in days. */
  maxResultAgeDays?: number | null;
  /** Default true — flip to false for "save as draft" wizard flows. */
  sendNow?: boolean;
}

export interface BulkImportInput {
  invitations: Array<{
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    /** The candidate's role, location and skills from the company's spreadsheet. */
    jobTitle?: string | null;
    location?: string | null;
    skills?: string[] | null;
  }>;
  cohortId?: string | null;
  programId?: string | null;
  destinationCountry?: string | null;
  branchName?: string | null;
  workLocation?: string | null;
  scenario?: 'pre_immigration' | 'already_abroad' | 'relocation_only' | 'integration_only' | null;
  /** BL-214 — default kind applied to every imported row. */
  invitationKind?: InvitationKind | null;
  /** TestIt: chosen reusable-test catalog ids applied to every row. */
  testitTests?: string[] | null;
  /** TestIt: optional tighter reuse-freshness window in days. */
  maxResultAgeDays?: number | null;
  sendNow?: boolean;
}

/**
 * What became of an invitation's email. An invitation is `pending` as soon as
 * it is sent — its link is live — whether or not the email went out, so the
 * status cannot say; this does.
 *
 *   • `queued`  — the email is on its way.
 *   • `skipped` — deliberately not sent (the invitee opted out, or the address
 *     belongs to a deleted account).
 *   • `failed`  — the dispatch failed. `refused`: sending it again as it stands
 *     will be refused again; `error`: a retry may clear it.
 */
export type InvitationEmailDispatch =
  | { outcome: 'queued' }
  | { outcome: 'skipped'; reason: string }
  | { outcome: 'failed'; reason: 'refused' | 'error' };

/** A sent (or re-sent) invitation, and what became of its email. */
export type SentInvitation = InvitationListItem & { emailDispatch: InvitationEmailDispatch };

export interface BulkImportResult {
  invited: number;
  drafted: number;
  duplicates: number;
  failed: number;
  errors: Array<{ email: string; error: string; reason: 'duplicate' | 'invalid' | 'system' }>;
  /**
   * Invitations that were created — they count in `invited` — whose email did
   * not go out. Empty when the import only drafts (`sendNow: false`).
   */
  emailFailures: Array<{ email: string; outcome: 'skipped' | 'failed'; reason: string }>;
}

/**
 * The template an invitation of each kind is sent with — and the one its
 * operator-written subject / body must have been written from. The server
 * refuses an invitation whose message was written for the other kind
 * (`TALENT_INVITATION_MESSAGE_MISMATCH`).
 */
export type InviteTemplateCode = 'vetting.invite' | 'vetting.invite_testit';

/**
 * Which journey the citizen shares with the inviting company (R5): one of the
 * preview's candidates, or a new journey built for it.
 */
export type AcceptJourneyChoice = { journeyId: string } | { new: true };

/** One journey the citizen may share — drawn like a card (R8). */
export interface AcceptJourneyCandidate {
  id: string;
  originCountry: string | null;
  destinationCountry: string;
  /** The citizen's name for it (D-F). */
  label: string | null;
  /** ISO — when it started. */
  startedAt: string;
  tierBadge: JourneyTierBadge;
  /** The OTHER companies live on it — sharing it shares it beside them. */
  otherCompanies: string[];
  /** The inviting company already works with the citizen on it (a re-invite). */
  isCompanyJourney: boolean;
  /** The citizen's current journey. */
  isCurrent: boolean;
  /** ISO — when its latest CheckUp was completed: what the company would see. */
  checkupCompletedAt: string | null;
}

/** One test a TestIt invitation asks for, as the accept screen lists it (D3). */
export interface AcceptAskedTest {
  testId: string;
  /** A result that satisfies it is already banked — accepting shares it. */
  done: boolean;
  /** ISO — when that result was taken; null until done. */
  takenAt: string | null;
}

/** `POST /talent-invitations/accept-preview` — decided by the server. */
export interface AcceptJourneyPreview {
  /** `null` when the caller may not accept this invitation. */
  company: { name: string } | null;
  /**
   * Who decides the journey: the citizen chooses among `candidates` (R5), or —
   * for a TestIt invitation, accepted as tests (D3) — the `server` attaches the
   * relationship and no choice is sent.
   */
  journeyChoice: 'citizen' | 'server';
  /** The journeys the citizen may share, the suggestion first. Empty when the server decides. */
  candidates: AcceptJourneyCandidate[];
  /** The server's suggestion, or where a TestIt invitation lands. `null`: a new journey is built. */
  suggestedJourneyId: string | null;
  /** A new journey for this company may be built — false only at the route cap. */
  canCreate: boolean;
  /** The most live journeys one origin → destination may hold. */
  cap: number;
  /** What accepting lets the company see — the consent the relationship holds once accepted. */
  grants: ConsentScope[];
  /** A TestIt invitation's asked tests; `completed`: nothing is left to take. `null` for a CheckUp one. */
  testit: { tests: AcceptAskedTest[]; completed: boolean } | null;
}

export interface AcceptTalentInvitationInput {
  token: string;
  /** Absent: the server's suggestion. */
  journeyChoice?: AcceptJourneyChoice;
}

export interface AcceptTalentInvitationResult {
  invitation: { id: string; status: InvitationStatus };
  talentId: string | null;
  journeyId: string | null;
  alreadyAccepted: boolean;
  /**
   * A TestIt acceptance (D3): each asked test and whether a result is banked.
   * `completed`: nothing is left to take — the company has been told, and the
   * accept surface confirms instead of routing to the tests. `null` for a
   * CheckUp invitation.
   */
  testit: { tests: AcceptAskedTest[]; completed: boolean } | null;
  /** Accepted on the strength of a claim (S5): what happened to the other account. */
  otherAccount?: 'retired' | 'kept' | 'retire_failed';
}

/** A claim code is on its way to the invited address (Citizen first run · S5). */
export interface InvitationClaimStarted {
  /** The invited address, masked. */
  sentTo: string;
  expiresInMinutes: number;
  resendAfterSeconds: number;
}

export interface DeclineTalentInvitationResult {
  invitation: { id: string; status: InvitationStatus };
  status: 'declined';
  alreadyDeclined: boolean;
}

// ── Service ──────────────────────────────────────────────────────────

export class TalentInvitationsService {
  constructor(private readonly client: TonnieClient) {}

  // Citizen-facing
  async preview(token: string): Promise<InvitationPreview> {
    return this.client.post<InvitationPreview>(API_ENDPOINTS.TALENT_INVITATIONS.PREVIEW, {
      token,
    });
  }

  /**
   * Pre-flight for the accept page: may this signed-in caller accept?
   *
   * Exists because the page had no way to know. It rendered an enabled "Accept
   * invitation" button for anyone signed in, and for a citizen holding an
   * invitation addressed to a different address that button was GUARANTEED to
   * fail.
   */
  eligibility(token: string): Promise<InvitationEligibility> {
    return this.client.post<InvitationEligibility>(API_ENDPOINTS.TALENT_INVITATIONS.ELIGIBILITY, {
      token,
    });
  }

  /**
   * Invitations waiting for the signed-in citizen. `includeDeferred` adds the
   * ones put off with "Not now" (each with its `deferredUntil`).
   */
  mine(options: { includeDeferred?: boolean } = {}): Promise<MyPendingInvitation[]> {
    const qs = options.includeDeferred ? '?includeDeferred=true' : '';
    return this.client.get<MyPendingInvitation[]>(`${API_ENDPOINTS.TALENT_INVITATIONS.MINE}${qs}`);
  }

  /** "Not now" — dismiss without deciding. NOT a decline. */
  defer(token: string): Promise<void> {
    return this.client.post<void>(API_ENDPOINTS.TALENT_INVITATIONS.DEFER, { token });
  }

  async accept(input: AcceptTalentInvitationInput): Promise<AcceptTalentInvitationResult> {
    return this.client.post<AcceptTalentInvitationResult>(
      API_ENDPOINTS.TALENT_INVITATIONS.ACCEPT,
      input.journeyChoice
        ? { token: input.token, journeyChoice: input.journeyChoice }
        : { token: input.token }
    );
  }

  /**
   * Claim an invitation sent to ANOTHER Tonnie account the citizen holds: send a
   * 6-digit code to the invited address. (Citizen first run · S5)
   */
  claimStart(token: string): Promise<InvitationClaimStarted> {
    return this.client.post<InvitationClaimStarted>(API_ENDPOINTS.TALENT_INVITATIONS.CLAIM_START, {
      token,
    });
  }

  /** Enter the code. On success the invitation is the signed-in account's to accept. */
  claimVerify(token: string, code: string): Promise<{ verified: true }> {
    return this.client.post<{ verified: true }>(API_ENDPOINTS.TALENT_INVITATIONS.CLAIM_VERIFY, {
      token,
      code,
    });
  }

  /** Before Accept: which of my journeys may I share with this company — or build a new one? */
  async acceptPreview(token: string): Promise<AcceptJourneyPreview> {
    return this.client.post<AcceptJourneyPreview>(API_ENDPOINTS.TALENT_INVITATIONS.ACCEPT_PREVIEW, {
      token,
    });
  }

  /**
   * Decline the invitation (terminal). Server verifies the authenticated
   * identity's verified email matches the invitation, then moves it to
   * `declined`. Idempotent on replay.
   */
  async decline(token: string): Promise<DeclineTalentInvitationResult> {
    return this.client.post<DeclineTalentInvitationResult>(
      API_ENDPOINTS.TALENT_INVITATIONS.DECLINE,
      { token }
    );
  }

  // Business-facing
  async list(filters?: {
    cohortId?: string;
    programId?: string;
    includeTerminal?: boolean;
    /** Adds the declined and expired invitations the company can act on. */
    includeClosed?: boolean;
  }): Promise<InvitationListItem[]> {
    const qs = filters
      ? '?' +
        new URLSearchParams(
          Object.fromEntries(
            Object.entries(filters).filter(([, v]) => v != null) as [string, string][]
          )
        ).toString()
      : '';
    return this.client.get<InvitationListItem[]>(`${API_ENDPOINTS.BUSINESS.INVITATIONS}${qs}`);
  }

  /**
   * Single-row read for the InvitationDetailDrawer. Returns the full
   * detail shape (status history + opportunity + token preview). 404
   * if the invitation has been soft-deleted or doesn't belong to the
   * caller's tenant.
   */
  async get(id: string): Promise<InvitationDetail> {
    return this.client.get<InvitationDetail>(API_ENDPOINTS.BUSINESS.INVITATION_BY_ID(id));
  }

  async create(input: CreateInvitationInput) {
    return this.client.post<{
      invitation: InvitationListItem;
      duplicate: boolean;
      /** Null when nothing was sent: a draft, or an invitation that already existed. */
      emailDispatch: InvitationEmailDispatch | null;
    }>(API_ENDPOINTS.BUSINESS.INVITATIONS, input);
  }

  async bulkImport(input: BulkImportInput): Promise<BulkImportResult> {
    return this.client.post<BulkImportResult>(API_ENDPOINTS.BUSINESS.INVITATION_BULK, input);
  }

  /**
   * Send (or resend) an invitation, optionally finalizing the
   * opportunity context in the same call. The opportunity is
   * persisted on the invitation row server-side and copied onto the
   * resulting journey_instance at acceptance time. All fields are
   * optional — the previous values on the row are preserved when a
   * field is omitted.
   *
   * Decision #4: pricingPlan is intentionally NOT exposed here;
   * invitations are always created/sent as 'free' (CheckUp only).
   * Sponsor flow promotes to 'enterprise' separately.
   */
  async send(
    id: string,
    opportunity?: {
      destinationCountry?: string | null;
      branchName?: string | null;
      workLocation?: string | null;
      scenario?:
        'pre_immigration' | 'already_abroad' | 'relocation_only' | 'integration_only' | null;
      customMessage?: string | null;
      customSubject?: string | null;
      customBody?: string | null;
      /** The template the subject / body were written from — see {@link InviteTemplateCode}. */
      customBodySource?: InviteTemplateCode | null;
      invitationKind?: InvitationKind | null;
      /** TestIt: chosen reusable-test catalog ids (required when kind='testit'). */
      testitTests?: string[] | null;
      /** TestIt: optional tighter reuse-freshness window in days. */
      maxResultAgeDays?: number | null;
    }
  ) {
    return this.client.post<SentInvitation>(
      API_ENDPOINTS.BUSINESS.INVITATION_SEND(id),
      opportunity ?? {}
    );
  }

  async resend(id: string) {
    return this.client.post<SentInvitation>(API_ENDPOINTS.BUSINESS.INVITATION_RESEND(id), {});
  }

  async revoke(id: string) {
    return this.client.post<InvitationListItem>(API_ENDPOINTS.BUSINESS.INVITATION_REVOKE(id), {});
  }

  async delete(id: string): Promise<{ success: boolean }> {
    return this.client.delete<{ success: boolean }>(API_ENDPOINTS.BUSINESS.INVITATION_BY_ID(id));
  }
}

export function createTalentInvitationsService(client: TonnieClient): TalentInvitationsService {
  return new TalentInvitationsService(client);
}
